import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Video, Plus, Calendar, Users, Save } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';

export default function WebinarAdminPage() {
  const [showForm, setShowForm] = useState(false);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['admin-webinars'],
    queryFn: () => api.admin.webinars.list(),
  });

  const webinars = (data?.data ?? []) as Record<string, unknown>[];

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Webinar Management</h1>
          <p className="text-sm text-gray-400 mt-1">{webinars.length} webinars</p>
        </div>
        <button onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-500 text-white rounded text-sm">
          <Plus className="w-4 h-4" /> Create Webinar
        </button>
      </div>

      {showForm && <CreateForm onClose={() => setShowForm(false)} qc={qc} />}

      {isLoading && <p className="text-gray-400">Loading...</p>}

      <div className="space-y-3">
        {webinars.map((w) => (
          <div key={w.webinarId as string} className="bg-gray-800/50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Video className="w-5 h-5 text-teal-400" />
                <div>
                  <div className="text-white font-medium">{w.title as string}</div>
                  <div className="text-xs text-gray-400 mt-0.5">
                    {w.presenterName as string} &middot; {w.scheduledAt ? new Date(w.scheduledAt as string).toLocaleString() : 'TBD'}
                    &middot; {w.durationMinutes as number}min
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1 text-xs text-gray-400">
                  <Users className="w-3 h-3" /> {w.registrantCount as number}{w.maxRegistrants ? `/${w.maxRegistrants}` : ''}
                </span>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                  w.status === 'upcoming' ? 'bg-blue-500/20 text-blue-400' :
                  w.status === 'live' ? 'bg-green-500/20 text-green-400' :
                  w.status === 'completed' ? 'bg-gray-600/20 text-gray-400' :
                  'bg-red-500/20 text-red-400'
                }`}>{w.status as string}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CreateForm({ onClose, qc }: { readonly onClose: () => void; readonly qc: ReturnType<typeof useQueryClient> }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [presenter, setPresenter] = useState('');
  const [presenterTitle, setPresenterTitle] = useState('');
  const [date, setDate] = useState('');
  const [duration, setDuration] = useState(60);
  const [maxReg, setMaxReg] = useState(100);

  const mutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.admin.webinars.create(data),
    onSuccess: () => { toast.success('Webinar created'); qc.invalidateQueries({ queryKey: ['admin-webinars'] }); onClose(); },
    onError: () => toast.error('Failed to create webinar'),
  });

  return (
    <div className="bg-gray-800 rounded-lg p-6 space-y-4">
      <h3 className="text-white font-medium">New Webinar</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title"
          className="bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white text-sm col-span-full" />
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description" rows={3}
          className="bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white text-sm col-span-full" />
        <input value={presenter} onChange={(e) => setPresenter(e.target.value)} placeholder="Presenter name"
          className="bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white text-sm" />
        <input value={presenterTitle} onChange={(e) => setPresenterTitle(e.target.value)} placeholder="Presenter title"
          className="bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white text-sm" />
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-gray-400" />
          <input type="datetime-local" value={date} onChange={(e) => setDate(e.target.value)}
            className="flex-1 bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white text-sm" />
        </div>
        <div className="flex gap-3">
          <div className="flex-1">
            <label className="text-xs text-gray-400">Duration (min)</label>
            <input type="number" value={duration} onChange={(e) => setDuration(parseInt(e.target.value))}
              className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white text-sm" />
          </div>
          <div className="flex-1">
            <label className="text-xs text-gray-400">Max registrants</label>
            <input type="number" value={maxReg} onChange={(e) => setMaxReg(parseInt(e.target.value))}
              className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white text-sm" />
          </div>
        </div>
      </div>
      <div className="flex gap-2">
        <button onClick={() => mutation.mutate({ title, description, presenterName: presenter, presenterTitle, scheduledAt: date ? new Date(date).toISOString() : null, durationMinutes: duration, maxRegistrants: maxReg })}
          disabled={!title || mutation.isPending}
          className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-500 text-white rounded text-sm disabled:opacity-50">
          <Save className="w-4 h-4" /> Create
        </button>
        <button onClick={onClose} className="px-4 py-2 text-gray-400 hover:text-white text-sm">Cancel</button>
      </div>
    </div>
  );
}
